import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useLang } from '../lib/i18n';
import { dayLong, hhmm, tl, TIERS } from '../lib/format';
import { icsFor, useOrder } from '../lib/orders';
import { toast } from '../lib/toast';
import { AppHeader, ErrorView, Loading } from '../components/AppHeader';
import { Ico, Poster } from '../components/brand';

export function Confirm() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const k = params.get('k');
  const nav = useNavigate();
  const { tx, lang } = useLang();
  const en = lang === 'en';
  const { order: o, error } = useOrder(id, k);
  if (error) return <ErrorView msg={error === 403 ? tx('Bu siparişi görme iznin yok.', 'You can’t view this order.') : tx('Sipariş bulunamadı.', 'Order not found.')} />;
  if (!o) return <Loading />;
  const e = o.event;
  const first = o.buyer.name.split(' ')[0];
  const paid = o.status === 'paid';
  const s0 = o.seats[0];
  const gate = s0?.gate ?? 'B';
  const seatLine = (s: (typeof o.seats)[number]) =>
    s.single ? `${s.row} · ${tx('Koltuk', 'Seat')} ${s.num}` : en ? `${s.sec === 'Parter' ? 'Stalls' : s.sec === 'Balkon' ? 'Balcony' : s.sec} · Row ${s.row} · ${s.num}` : `${s.sec} · ${s.row} sırası · ${s.num}`;
  const ticketUrl = `/bilet/${o.id}${k ? `?k=${k}` : ''}`;

  return (
    <div className="scroll">
      <AppHeader cur="" />
      <div className="wrap" style={{ maxWidth: 760, paddingTop: 20, paddingBottom: 48 }}>
        {paid ? (
          <span className="badge ok">
            <Ico n="check" cls="ico-s" /> {tx('Ödeme alındı', 'Payment received')}
          </span>
        ) : (
          <span className="badge bad">{o.status === 'refunded' ? tx('İade edildi', 'Refunded') : tx('Ödeme tamamlanmadı', 'Payment not completed')}</span>
        )}
        <h1 className="d-xl" style={{ margin: '16px 0 10px' }}>
          {paid ? tx(`Biletlerin hazır, ${first}.`, `Your tickets are ready, ${first}.`) : o.status === 'refunded' ? tx('Bu sipariş iade edildi.', 'This order was refunded.') : tx('Sipariş tamamlanmadı.', 'Order not completed.')}
        </h1>
        {paid && (
          <p className="t-l" style={{ margin: 0, color: 'var(--ink-2)' }}>
            {en ? (
              <>
                {o.tickets.length} {o.tickets.length === 1 ? 'ticket is' : 'tickets are'} saved under <b>My tickets</b> on this device for <b>{o.buyer.email}</b>. Just show the QR code on your phone at the door.
              </>
            ) : (
              <>
                {o.tickets.length} bilet <b>{o.buyer.email}</b> adına bu cihazda <b>Biletlerim</b>’e eklendi. Kapıda telefonundaki QR kodu okutman yeterli.
              </>
            )}
          </p>
        )}
        {paid && (
          <p className="t-s muted" style={{ margin: '8px 0 0' }}>
            {tx('Demo modunda e-posta ve SMS gönderilmez.', 'In demo mode no email or SMS is sent.')}
          </p>
        )}
        <div className="panel" style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="mini-ev">
            <Poster poster={e.poster} title={e.short || e.title} />
            <div>
              <div className="cap muted">
                {dayLong(o.session.startsAt, lang)} · {hhmm(o.session.startsAt)}
              </div>
              <div className="d-s">{e.title}</div>
              <div className="t-s muted">
                {o.venue.name} · {tx('Kapılar', 'Doors')} {e.doors}
              </div>
            </div>
          </div>
          <hr className="rule" />
          {o.seats.map((s) => (
            <div className="line" key={s.id}>
              <span>{seatLine(s)}</span>
              <span className="muted">{en ? TIERS[s.tier].en : TIERS[s.tier].tr}</span>
            </div>
          ))}
          <div className="line big">
            <span>{paid ? tx('Ödenen', 'Paid') : tx('Tutar', 'Amount')}</span>
            <span className="num">{tl(o.totalKurus, lang)}</span>
          </div>
          <div className="line muted t-s" style={{ padding: 0 }}>
            <span>{tx('Sipariş no', 'Order no')}</span>
            <span className="mono">{o.no}</span>
          </div>
        </div>
        {paid && (
          <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
              <button type="button" className="btn btn-primary btn-lg" onClick={() => nav(ticketUrl)}>
                <Ico n="ticket" /> {tx('Biletlerimi göster', 'Show my tickets')}
              </button>
              <button
                type="button"
                className="btn btn-line btn-lg"
                onClick={() => {
                  icsFor(o);
                  toast(tx('Takvim dosyası indirildi', 'Calendar file downloaded'));
                }}
              >
                <Ico n="cal" /> {tx('Takvime ekle', 'Add to calendar')}
              </button>
            </div>
            <div className="sec-h">
              <h2 className="d-s">{tx('Gösteri günü', 'On the day')}</h2>
            </div>
            <ol className="wsteps">
              <li>
                <span className="k">1</span>
                <div>
                  <b>{tx(`Kapılar ${e.doors}’da açılır`, `Doors open at ${e.doors}`)}</b>
                  <span>{tx('Oyun başladıktan sonra salona yalnızca arada girilebilir.', 'After the show starts, latecomers are seated at the interval.')}</span>
                </div>
              </li>
              <li>
                <span className="k">2</span>
                <div>
                  <b>{tx('QR kodunu hazırla', 'Have your QR code ready')}</b>
                  <span>{tx('İnternet olmasa da çalışır. Ekran parlaklığını artırırsan daha hızlı okunur.', 'Works without internet. Turn up the brightness for a faster scan.')}</span>
                </div>
              </li>
              <li>
                <span className="k">3</span>
                <div>
                  <b>{tx(`Kapı ${gate}’den gir`, `Use door ${gate}`)}</b>
                  <span>
                    {s0 && !s0.single
                      ? tx(`${s0.sec} ${s0.row} sırası için en yakın kapı. Asansör girişin sağında.`, `Closest door for ${s0.sec} row ${s0.row}. The lift is right of the entrance.`)
                      : tx('Localar için en yakın kapı. Asansör girişin sağında.', 'Closest door for the boxes. The lift is right of the entrance.')}
                  </span>
                </div>
              </li>
            </ol>
          </>
        )}
        {!paid && (
          <button type="button" className="btn btn-primary" style={{ marginTop: 20 }} onClick={() => nav(`/koltuk/${o.session.id}`)}>
            {tx('Koltuk haritasına dön', 'Back to the seat map')}
          </button>
        )}
      </div>
    </div>
  );
}
